import { css, html, LitElement } from "lit"
import { unsafeHTML } from "lit/directives/unsafe-html.js"
import "./CartePokemonPlanning.js"
import { palettePlanning } from "./stylePlanning.js"

/**
 * Bloc "Planning hebdomadaire" de la page d'accueil.
 * Reçoit un planning déjà analysé (parserPlanningHebdomadaire) ; si aucun jour
 * n'est disponible, retombe sur le rendu markdown brut fourni en secours.
 */
export default class PlanningHebdomadaire extends LitElement {
  static properties = {
    semaine: { type: String },
    jours: { type: Array },
    texteComplementaireHtml: { type: String },
    fallbackHtml: { type: String },
  }

  static styles = [
    palettePlanning,
    css`
      :host {
        display: block;
      }

      .eyebrow {
        text-transform: uppercase;
        letter-spacing: 0.14em;
        font-size: 1.2rem;
        color: var(--planning-texte-doux);
        margin: 0 0 0.4rem;
      }

      h2 {
        margin: 0 0 0.3rem;
      }

      .sous-titre {
        color: var(--planning-texte-doux);
        font-size: 1.4rem;
        margin: 0 0 2.4rem;
      }

      .jour {
        margin-bottom: 3rem;
      }

      .jour:last-of-type {
        margin-bottom: 1rem;
      }

      .etiquette-jour {
        display: inline-flex;
        align-items: center;
        gap: 0.6rem;
        font-size: 1.2rem;
        letter-spacing: 0.06em;
        color: var(--planning-texte-doux);
        background-color: var(--planning-surface);
        border: 1px solid var(--planning-bordure);
        padding: 0.4rem 1rem;
        border-radius: 5rem;
        margin-bottom: 1rem;
      }

      .etiquette-jour .point {
        width: 0.6rem;
        height: 0.6rem;
        border-radius: 50%;
        background-color: var(--planning-texte-doux);
      }

      .titre-evenement {
        margin: 0 0 1.2rem;
        font-size: 1.8rem;
      }

      .liste-cartes {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .complement {
        margin-top: 2.4rem;
        padding-top: 2rem;
        border-top: 1px solid var(--planning-bordure);
        font-size: 1.4rem;
        color: var(--planning-texte-doux);
      }

      /* Mise en avant héritée de l'ancien rendu markdown (fallback). */
      .pc {
        font-weight: bold;
        color: var(--couleur-texte);
      }
    `,
  ]

  constructor() {
    super()
    this.semaine = ""
    this.jours = []
    this.texteComplementaireHtml = ""
    this.fallbackHtml = ""
  }

  abreviationJour(jour) {
    return String(jour || "")
      .slice(0, 3)
      .toUpperCase()
  }

  numeroJour(date) {
    return (String(date || "").match(/\d+/) || [""])[0]
  }

  render() {
    if (!this.jours?.length) {
      return html`
        <p class="eyebrow">Cette semaine</p>
        <h2>Planning hebdomadaire</h2>
        ${this.fallbackHtml ? unsafeHTML(this.fallbackHtml) : html`<p class="sous-titre">Planning indisponible pour le moment.</p>`}
      `
    }

    return html`
      <p class="eyebrow">Cette semaine</p>
      <h2>Planning hebdomadaire</h2>
      ${this.semaine ? html`<p class="sous-titre">${this.semaine}</p>` : ""}
      ${this.jours.map((jour) => this.genererJour(jour))}
      ${this.texteComplementaireHtml ? html`<div class="complement">${unsafeHTML(this.texteComplementaireHtml)}</div>` : ""}
    `
  }

  genererJour(jour) {
    const numero = this.numeroJour(jour.date)
    return html`
      <section class="jour">
        <span class="etiquette-jour">
          <span class="point"></span>${this.abreviationJour(jour.jour)}${numero ? html` ${numero}` : ""}
        </span>
        ${jour.evenement ? html`<h3 class="titre-evenement">${jour.evenement}</h3>` : ""}
        <div class="liste-cartes">
          ${jour.pokemons.map((pokemon) => html`<p-carte-pokemon-planning .pokemon=${pokemon}></p-carte-pokemon-planning>`)}
        </div>
      </section>
    `
  }
}
customElements.define("p-planning-hebdomadaire", PlanningHebdomadaire)
